import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Users, Smartphone, Crown, RotateCcw, Volume2, VolumeX, Zap, Bell, Hand, Heart, Shuffle, SkipForward, Star, Trophy, Flame, Skull, Disc3, Music, Tv, Check, X, Laugh } from "lucide-react";
import { api } from "@/lib/api";
import { audio } from "@/lib/audio";
import { MemePickTV, MemeVoteTV, MemeRevealTV } from "@/components/game/memeTV";
import { Avatar, OPTIONS, ShapeIcon, TimerRing, useCountdown, useRoomState } from "@/lib/gameUi";
import { useSpotify } from "@/components/game/jukebox";

const MODE_ICON = { fill_your_boots: Zap, buzzer_beater: Bell, point_snatcher: Hand, last_man_standing: Heart, hot_streaker: Flame, grassy_knoall: Skull, nominator: Crown, hit_that: Music, meme_caption: Laugh, random: Shuffle };
const MODE_HOWTO = {
  meme_caption: ["A real meme appears on the TV", "Play your funniest caption card from your phone", "Everyone votes — 100 per vote, +250 for the winner"],
  fill_your_boots: ["Everyone answers every question", "Faster correct answers bank more points"],
  buzzer_beater: ["Everyone races to answer", "Only the FIRST correct answer scores"],
  point_snatcher: ["First correct answer wins the points", "…and gets to STEAL points from a rival"],
  last_man_standing: ["3 lives each", "A wrong answer costs a life — last survivor wins big"],
  hot_streaker: ["Same question for everyone", "One wrong answer and you're OUT", "Each correct is worth more — nail all 8 for a bonus"],
  grassy_knoall: ["2 lives each", "The fastest correct answer takes a life from a rival", "Last one standing wins 1000"],
  nominator: ["The leader plays the question…", "…or passes it to a rival to answer"],
  hit_that: ["Listen to a snippet of a hit song", "Answer: the artist, title or release year", "Faster correct answers bank more points"],
};
const HOT_STREAK_PTS = [50, 100, 150, 200, 250, 300, 350, 400];

const MODE_NAMES = {
  fill_your_boots: "Fill your Boots",
  buzzer_beater: "Buzzer Beater",
  point_snatcher: "Nice Snatch",
  last_man_standing: "Last Man Standing",
  hot_streaker: "Hot Streaker",
  grassy_knoall: "The Grassy Kno-all",
  nominator: "The Nominator",
  hit_that: "I'd Hit That",
  meme_caption: "Caption the Meme",
  random: "🎲 Surprise Mode",
};
const MODE_LIST = [
  { key: "fill_your_boots", blurb: "Everyone plays, speed counts" },
  { key: "buzzer_beater", blurb: "First correct answer takes it all" },
  { key: "point_snatcher", blurb: "Win points & steal from a rival" },
  { key: "last_man_standing", blurb: "3 lives — last survivor wins big" },
  { key: "hot_streaker", blurb: "One slip and you're out — build a streak" },
  { key: "grassy_knoall", blurb: "Fastest answer assassinates a rival" },
  { key: "nominator", blurb: "The leader plays it or passes the buck" },
  { key: "hit_that", blurb: "Name the hit song, artist or year" },
  { key: "meme_caption", blurb: "Caption real memes, everyone votes" },
];

export default function HostGame() {
  const _params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const spectateCode = (_params.get("code") || "").toUpperCase();
  const spectator = !!spectateCode;
  const [code, setCode] = useState(spectator ? spectateCode : null);
  const [hostToken, setHostToken] = useState(null);
  const [muted, setMuted] = useState(audio.isMuted());
  const [soundReady, setSoundReady] = useState(audio.isMuted());
  const inited = useRef(false);

  useEffect(() => {
    if (spectator) return;        // a cast/extra screen mirrors an existing room — never create one
    if (inited.current) return;
    inited.current = true;
    (async () => {
      const sc = localStorage.getItem("tp_tv_code"), st = localStorage.getItem("tp_tv_ht");
      if (sc && st) {
        try { const r = await api.get(`/rooms/${sc}/state`); if (r.data && r.data.status !== "finished") { setCode(sc); setHostToken(st); return; } } catch {}
      }
      const res = await api.post("/rooms", {});
      localStorage.setItem("tp_tv_code", res.data.code); localStorage.setItem("tp_tv_ht", res.data.host_token);
      setCode(res.data.code); setHostToken(res.data.host_token);
    })();
  }, []);

  // enable audio on first interaction (browser autoplay); also attempt immediately
  useEffect(() => {
    if (!audio.isMuted()) { audio.resume(); audio.startMusic(); }
    const onFirst = () => { audio.unlock(); if (!audio.isMuted()) audio.startMusic(); window.removeEventListener("pointerdown", onFirst); };
    window.addEventListener("pointerdown", onFirst);
    return () => window.removeEventListener("pointerdown", onFirst);
  }, []);

  const { state } = useRoomState(code, null);

  useEffect(() => { audio.setNarrationEnabled(state?.settings?.narration !== false); }, [state?.settings?.narration]);
  // Cap background music low once the game is underway (narration must cut through).
  useEffect(() => { audio.setInRound(!!state && !["setup", "lobby"].includes(state.status)); }, [state?.status]);
  // Pre-cache the NEXT question's narration so it plays the instant that question appears.
  useEffect(() => { if (state?.next_spoken) audio.prefetchNarration(state.next_spoken); }, [state?.next_spoken]);
  // On a spectator/cast screen there's no Spotify SDK, so suppress the local party
  // music during the music round (no snippet to clash with) to keep things calm.
  useEffect(() => { if (spectator) audio.setMusicSuppressed(state?.round?.mode === "hit_that"); }, [spectator, state?.round?.mode]);

  const toggleSound = () => {
    audio.unlock();
    const m = !muted; audio.setMuted(m); setMuted(m); setSoundReady(true);
    if (!m) audio.startMusic(); else audio.stopNarration();
  };
  const enableSound = () => { audio.unlock(); audio.setMuted(false); setMuted(false); audio.startMusic(); setSoundReady(true); setTimeout(() => audio.narrate("Narration is on. Let's play Gimme a sQuizz!"), 200); };
  if (!code || (!spectator && !hostToken) || !state) return <div className="neon-bg fixed inset-0 flex flex-col items-center justify-center gap-4"><Loader2 className="w-10 h-10 text-[#FF2A85] animate-spin" /><p className="text-white/70 font-head">{spectator ? "Connecting to the game…" : "Starting up…"}</p></div>;

  return (
    <TVStage>
      {spectator && (
        <div data-testid="tv-cast-badge" className="absolute top-8 left-10 z-50 glass rounded-full px-5 py-2.5 border border-[#00F0FF]/40 flex items-center gap-2.5">
          <Tv className="w-5 h-5 text-[#00F0FF]" /><span className="font-head font-bold text-white/85 text-sm">Extra screen · <span className="text-[#00F0FF]">{code}</span></span>
        </div>
      )}
      {!soundReady && !muted && (
        <button data-testid="tv-enable-sound" onClick={enableSound} className="absolute bottom-10 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 glass rounded-full pl-4 pr-6 py-3 border border-[#00FF66]/40 glow-green">
          <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 1.6 }} className="w-10 h-10 rounded-full bg-[#00FF66]/20 flex items-center justify-center shrink-0"><Volume2 className="w-6 h-6 text-[#00FF66]" /></motion.span>
          <span className="text-left"><span className="block font-display font-black text-lg text-white leading-tight">Tap to enable sound & narration</span><span className="block font-head text-white/55 text-sm">Questions are read aloud on this screen</span></span>
        </button>
      )}
      <button data-testid="tv-sound-toggle" onClick={toggleSound} className="absolute top-8 right-10 z-50 glass rounded-full w-14 h-14 flex items-center justify-center border border-white/15">
        {muted ? <VolumeX className="w-7 h-7 text-white/60" /> : <Volume2 className="w-7 h-7 text-[#00FF66]" />}
      </button>
      {!spectator && <SpotifyTV code={code} hostToken={hostToken} status={state.status} roundMode={state.round?.mode} />}
      <div className="relative z-10 h-full w-full px-16 py-12">
        {state.status === "setup" && (spectator ? <SpectatorWaiting /> : <HostQR code={code} hostToken={hostToken} />)}
        {state.status === "lobby" && <JoinLobby state={state} code={code} hostToken={hostToken} />}
        {state.status === "round_intro" && <RoundIntro state={state} />}
        {state.status === "nominate" && <NominateTV state={state} />}
        {state.status === "assassinate" && <AssassinateTV state={state} />}
        {state.status === "question" && <QuestionView state={state} code={code} hostToken={hostToken} spectator={spectator} />}
        {state.status === "steal" && <StealTV state={state} />}
        {state.status === "reveal" && <RevealView state={state} />}
        {state.status === "meme_pick" && <MemePickTV state={state} />}
        {state.status === "meme_vote" && <MemeVoteTV state={state} />}
        {state.status === "meme_reveal" && <MemeRevealTV state={state} />}
        {state.status === "bonus_intro" && <BonusIntroTV state={state} />}
        {state.status === "bonus" && <BonusTV state={state} />}
        {state.status === "bonus_reveal" && <BonusRevealTV state={state} />}
        {state.status === "finished" && <Finished state={state} />}
      </div>
    </TVStage>
  );
}

// Fixed 1920x1080 "TV canvas" scaled to fit any screen within a title-safe
// margin (5% total). Content is authored at this resolution and scaled as one
// unit, so nothing can ever bleed off a TV's overscan edges — on any TV.
function TVStage({ children }) {
  const [scale, setScale] = useState(() => Math.min((typeof window !== "undefined" ? window.innerWidth : 1920) * 0.95 / 1920, (typeof window !== "undefined" ? window.innerHeight : 1080) * 0.95 / 1080));
  useLayoutEffect(() => {
    const compute = () => setScale(Math.min((window.innerWidth * 0.95) / 1920, (window.innerHeight * 0.95) / 1080));
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    const t = setTimeout(compute, 300);
    return () => { window.removeEventListener("resize", compute); window.removeEventListener("orientationchange", compute); clearTimeout(t); };
  }, []);
  return (
    <div className="neon-bg fixed inset-0 overflow-hidden flex items-center justify-center">
      <div style={{ width: 1920 * scale, height: 1080 * scale, position: "relative", overflow: "hidden", flex: "0 0 auto" }}>
        <div style={{ position: "absolute", top: 0, left: 0, width: 1920, height: 1080, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function HostControlsOverlay() { return null; }

// Spotify Web Playback SDK lives on the TV tab so music comes out of the TV.
function SpotifyTV({ code, hostToken, status, roundMode }) {
  const sp = useSpotify(code, 3000);
  const playerRef = useRef(null);
  const preRound = useRef(null);
  const volRef = useRef({ base: 0.6, duck: 0.22 });
  const BASE_VOL = 0.6;
  const connected = sp?.connected && sp?.is_premium;

  useEffect(() => {
    if (!connected || playerRef.current) return;
    const init = () => {
      if (!window.Spotify || playerRef.current) return;
      const player = new window.Spotify.Player({
        name: "Gimme a sQuizz TV",
        getOAuthToken: async (cb) => {
          try { const r = await api.get(`/rooms/${code}/spotify/token`, { params: { host_token: hostToken } }); cb(r.data.access_token); } catch (e) { /* ignore */ }
        },
        volume: BASE_VOL,
      });
      // Re-register on every ready so the device id is never stale after a reconnect.
      player.addListener("ready", async ({ device_id }) => {
        try { await api.post(`/rooms/${code}/spotify/device`, { host_token: hostToken, device_id }); } catch (e) { /* ignore */ }
      });
      player.connect();
      playerRef.current = player;
      audio.setDuckCallback((active) => { try { playerRef.current && playerRef.current.setVolume(active ? volRef.current.duck : volRef.current.base); } catch (e) { /* ignore */ } });
    };
    if (window.Spotify) { init(); }
    else {
      if (!document.getElementById("spotify-sdk")) {
        const s = document.createElement("script"); s.id = "spotify-sdk"; s.src = "https://sdk.scdn.co/spotify-player.js"; s.async = true; document.body.appendChild(s);
      }
      const prev = window.onSpotifyWebPlaybackSDKReady;
      window.onSpotifyWebPlaybackSDKReady = () => { if (prev) prev(); init(); };
    }
  }, [connected, code, hostToken]);

  useEffect(() => () => { audio.setDuckCallback(null); if (playerRef.current) { try { playerRef.current.disconnect(); } catch (e) { /* ignore */ } } }, []);

  // Volume policy: the "I'd Hit That" snippet is the question so it's loud; any
  // other in-round jukebox playback is capped mellow so narration cuts through;
  // pre-game lobby plays at full. Duck further whenever narration is speaking.
  useEffect(() => {
    const inRound = !["setup", "lobby"].includes(status);
    const snippet = roundMode === "hit_that" && status === "question";
    const base = snippet ? 0.7 : (inRound ? 0.26 : BASE_VOL);
    const duck = snippet ? 0.5 : (inRound ? 0.15 : 0.22);
    volRef.current = { base, duck };
    try { playerRef.current && playerRef.current.setVolume(base); } catch (e) { /* ignore */ }
  }, [status, roundMode]);

  // During "I'd Hit That", keep the party playlist fully paused except while a
  // snippet is being played. The previous snippet is left playing through the
  // reveal/gap so there's no silence until the next question's countdown swaps it.
  useEffect(() => {
    const p = playerRef.current; if (!p) return;
    if (roundMode === "hit_that" && status !== "question" && status !== "reveal") { p.pause().catch(() => {}); }
  }, [status, roundMode]);

  // Snapshot playback entering a hit_that round; resume that exact track when it ends.
  useEffect(() => {
    const p = playerRef.current; if (!p) return;
    (async () => {
      if (roundMode === "hit_that" && preRound.current === null) {
        try {
          const st = await p.getCurrentState();
          const t = st && st.track_window && st.track_window.current_track;
          preRound.current = t ? { uri: t.uri, position: st.position || 0 } : { uri: null };
        } catch (e) { preRound.current = { uri: null }; }
      } else if (roundMode && roundMode !== "hit_that" && preRound.current !== null) {
        const snap = preRound.current; preRound.current = null;
        if (snap.uri) api.post(`/rooms/${code}/spotify/play-track`, { host_token: hostToken, uri: snap.uri, position_ms: Math.floor(snap.position || 0) }).catch(() => {});
        else api.post(`/rooms/${code}/spotify/playback`, { host_token: hostToken, action: "pause" }).catch(() => {});
      }
    })();
  }, [roundMode, code, hostToken]);

  // Mute the built-in party music for the WHOLE hit_that round (no clunky resume
  // between snippets), and whenever Spotify itself is playing.
  const inHitThat = roundMode === "hit_that";
  const spotifyPlaying = !!(sp?.connected && sp?.now_playing?.is_playing);
  useEffect(() => { audio.setMusicSuppressed(inHitThat || spotifyPlaying); }, [inHitThat, spotifyPlaying]);
  useEffect(() => () => { audio.setMusicSuppressed(false); }, []);

  if (!sp || !sp.connected) return null;
  if (roundMode === "hit_that" || ["question", "reveal", "bonus", "bonus_reveal"].includes(status)) return null;
  const np = sp.now_playing;
  return (
    <div className="absolute bottom-8 left-10 z-50 flex items-center gap-3 glass rounded-2xl pl-2 pr-5 py-2 border border-[#1DB954]/40 max-w-md">
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 flex items-center justify-center shrink-0">{np?.image ? <img src={np.image} className="w-full h-full object-cover" alt="" /> : <Disc3 className="w-6 h-6 text-[#1DB954]" />}</div>
      <div className="min-w-0">
        <p className="font-head text-[#1DB954] text-xs uppercase tracking-widest font-bold">{sp.is_premium ? "Jukebox" : "Spotify (Premium needed)"}</p>
        {np ? <><p className="font-head font-bold text-white truncate">{np.name}</p><p className="font-head text-white/50 text-sm truncate">{np.artist}</p></>
            : <p className="font-head text-white/60 text-sm">Host adds the soundtrack 🎵</p>}
      </div>
    </div>
  );
}

function HostQR({ code, hostToken }) {
  const url = useMemo(() => `${window.location.origin}/control?code=${code}&ht=${hostToken}`, [code, hostToken]);
  const steps = ["Scan with your phone camera", "Choose rounds, modes & difficulty", "Open the lobby — players pile in!"];
  return (
    <div className="h-full flex items-stretch gap-14">
      {/* Left: branding + game modes */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <motion.h1 initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 140 }} className="font-display font-black uppercase leading-[0.8] text-[7.5rem] tracking-tight mb-7">
          <span className="text-white text-glow-magenta">Gimme a </span><span className="bg-gradient-to-r from-[#FF2A85] via-[#8B5CF6] to-[#00F0FF] bg-clip-text text-transparent">sQuizz</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="font-head text-3xl text-white/70 max-w-3xl leading-snug mb-10">
          The party trivia showdown where <span className="text-[#00F0FF] font-bold">your phone is the controller</span> and the big screen is the stage. Snap a selfie, grab a nickname, and battle for the crown. <Crown className="inline w-7 h-7 text-[#FFD700] -mt-1" fill="#FFD700" />
        </motion.p>
        <p className="font-head uppercase tracking-[0.3em] text-[#FFD700] text-lg mb-5 flex items-center gap-2.5"><Flame className="w-6 h-6" /> 8 Game modes in the mix</p>
        <div className="grid grid-cols-2 gap-4">
          {MODE_LIST.map((m, i) => { const Icon = MODE_ICON[m.key] || Zap; return (
            <motion.div key={m.key} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.25 + i * 0.06 }} className="glass rounded-2xl px-5 py-4 border border-white/10 flex items-center gap-4">
              <span className="shrink-0 w-14 h-14 rounded-2xl bg-[#FF2A85]/15 flex items-center justify-center"><Icon className="w-7 h-7 text-[#FF2A85]" /></span>
              <div className="min-w-0"><p className="font-display font-black text-white text-2xl leading-tight truncate">{MODE_NAMES[m.key]}</p><p className="font-head text-white/55 text-lg leading-tight truncate">{m.blurb}</p></div>
            </motion.div>); })}
        </div>
      </div>
      {/* Right: host QR + steps */}
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="shrink-0 w-[540px] flex flex-col justify-center">
        <div className="glass glow-magenta rounded-[2rem] p-9 border-2 border-[#FFD700]/50 flex flex-col items-center w-full">
          <p className="font-head font-bold text-[#FFD700] uppercase tracking-widest text-xl mb-6 flex items-center gap-2.5"><Crown className="w-6 h-6" fill="#FFD700" /> Host — scan to set up</p>
          <div data-testid="tv-host-qr-code" className="bg-white p-6 rounded-3xl"><QRCodeSVG value={url} size={400} level="M" /></div>
          <p className="mt-6 font-head text-white/65 text-center text-xl leading-snug">Pick the rounds, modes &amp; difficulty, then open the lobby for everyone else.</p>
        </div>
        <div className="mt-9 w-full space-y-4">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-4">
              <span className="shrink-0 w-12 h-12 rounded-full bg-[#00F0FF]/15 text-[#00F0FF] font-display font-black text-xl flex items-center justify-center">{i + 1}</span>
              <span className="font-head font-semibold text-white/85 text-2xl">{s}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function JoinLobby({ state, code, hostToken }) {
  const joinUrl = useMemo(() => `${window.location.origin}/play?code=${code}`, [code]);
  const hostUrl = useMemo(() => `${window.location.origin}/control?code=${code}&ht=${hostToken}`, [code, hostToken]);
  const players = state.players;
  const rounds = state.settings?.rounds || [];
  const steps = ["Scan the pink QR with your phone", "Snap a selfie & pick a nickname", "Answer on your phone — eyes on the TV!"];
  return (
    <div className="h-full px-10 py-6 flex flex-col overflow-hidden">
      <header className="flex items-center justify-between shrink-0">
        <h1 className="font-display font-black text-6xl uppercase tracking-tight leading-none"><span className="text-white text-glow-magenta">Gimme a</span> <span className="text-[#00F0FF] text-glow-cyan">sQuizz</span></h1>
        <div className="flex items-center gap-3 mr-16">
          <div data-testid="tv-lobby-room-code" className="glass rounded-full px-6 py-2.5 border border-[#FFD700]/40 flex items-center gap-2.5"><span className="font-head text-white/50 text-sm uppercase tracking-widest">Code</span><span className="font-display font-black text-3xl tracking-[0.2em] text-[#FFD700]">{code}</span></div>
          <div data-testid="tv-player-count" className="glass rounded-full px-5 py-2.5 flex items-center gap-2 border border-white/10"><Users className="w-6 h-6 text-[#FFD700]" /><span className="font-display font-bold text-2xl">{state.player_count}<span className="text-white/40 text-lg">/12</span></span></div>
        </div>
      </header>

      {/* Centerpiece: how-to / QR codes / tonight's rounds */}
      <div className="flex-1 min-h-0 flex items-center justify-center gap-8">
        <div className="w-72 shrink-0">
          <p className="font-head uppercase tracking-[0.3em] text-[#00FF66] text-sm mb-4">How to play</p>
          <div className="space-y-3.5">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="shrink-0 w-9 h-9 rounded-full bg-[#00FF66]/15 text-[#00FF66] font-display font-black flex items-center justify-center">{i + 1}</span>
                <span className="font-head font-semibold text-white/80 text-lg leading-tight">{s}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="glass rounded-3xl p-6 border border-white/10 glow-cyan flex flex-col items-center shrink-0">
          <p className="font-head uppercase tracking-[0.25em] text-[#00F0FF] text-base mb-3 flex items-center gap-1.5"><Smartphone className="w-5 h-5" /> Players — join here</p>
          <div data-testid="tv-lobby-qr-code" className="bg-white p-4 rounded-2xl"><QRCodeSVG value={joinUrl} size={300} level="M" /></div>
        </div>
        <div className="glass rounded-3xl p-6 border border-[#FFD700]/40 glow-yellow flex flex-col items-center shrink-0">
          <p className="font-head uppercase tracking-[0.25em] text-[#FFD700] text-base mb-3 flex items-center gap-1.5"><Crown className="w-5 h-5" fill="#FFD700" /> Host — control here</p>
          <div data-testid="tv-host-qr-code" className="bg-white p-4 rounded-2xl"><QRCodeSVG value={hostUrl} size={300} level="M" /></div>
        </div>
        <div className="w-72 shrink-0">
          <p className="font-head uppercase tracking-[0.3em] text-[#FF2A85] text-sm mb-4">Tonight's rounds</p>
          <div className="space-y-2 overflow-hidden">
            {rounds.map((rd, i) => { const Icon = MODE_ICON[rd.mode] || Shuffle; return (
              <div key={i} className="glass rounded-xl px-3 py-2 border border-white/10 flex items-center gap-2.5">
                <span className="font-display font-black text-white/40 text-sm w-5 text-center shrink-0">{i + 1}</span>
                <Icon className="w-5 h-5 text-[#FF2A85] shrink-0" />
                <span className="font-head font-bold text-white/85 text-base truncate">{MODE_NAMES[rd.mode] || rd.mode}</span>
              </div>); })}
          </div>
        </div>
      </div>

      {/* Bottom third: players as they join */}
      <div className="shrink-0 h-[28%] border-t border-white/10 pt-3 flex flex-col">
        <p className="text-center font-head uppercase tracking-[0.3em] text-white/45 text-sm mb-3 shrink-0">{players.length === 0 ? "Waiting for players…" : `${state.player_count} in the lobby`} · Host taps Start Game when ready</p>
        <div className="flex-1 min-h-0 flex items-start justify-center overflow-hidden">
          {players.length === 0 ? (
            <div className="flex items-center gap-3 text-white/70"><Loader2 className="w-7 h-7 text-[#FF2A85] animate-spin" /><p className="font-display font-bold text-xl">Scan a QR code to jump in!</p></div>
          ) : (
            <div className="flex flex-wrap items-start justify-center gap-4">
              <AnimatePresence>
                {players.map((p) => (
                  <motion.div key={p.id} data-testid={`tv-lobby-player-${p.id}`} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 16 }} className="flex flex-col items-center gap-1.5 w-24">
                    <Avatar player={p} size={64} /><span className="font-head font-bold text-white text-sm truncate max-w-full">{p.name}</span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RoundIntro({ state }) {
  const r = state.round;
  const Icon = MODE_ICON[r.mode] || Zap;
  const howto = MODE_HOWTO[r.mode] || [r.mode_desc];
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-head uppercase tracking-[0.4em] text-cyan-300 text-base mb-4">Round {r.index + 1} of {r.total}</motion.p>
      <motion.div initial={{ scale: 0.5, opacity: 0, rotate: -15 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 160 }} className="w-24 h-24 rounded-[2rem] bg-[#FF2A85]/20 glow-magenta flex items-center justify-center mb-6"><Icon className="w-12 h-12 text-[#FF2A85]" /></motion.div>
      <motion.h1 initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 }} className="font-display font-black text-5xl lg:text-7xl uppercase tracking-tight bg-gradient-to-r from-[#FF2A85] via-[#8B5CF6] to-[#00F0FF] bg-clip-text text-transparent">{r.mode_name}</motion.h1>
      <div className="mt-7 flex flex-col gap-2.5 max-w-2xl w-full">
        {howto.map((line, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 + i * 0.25 }} className="glass rounded-2xl px-5 py-3 border border-white/10 flex items-center gap-3 text-left">
            <span className="shrink-0 w-7 h-7 rounded-full bg-[#00FF66]/20 text-[#00FF66] font-display font-black flex items-center justify-center text-sm">{i + 1}</span>
            <span className="font-head font-semibold text-lg lg:text-xl text-white/85">{line}</span>
          </motion.div>
        ))}
      </div>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="font-head text-white/40 mt-8 flex items-center gap-2"><Smartphone className="w-5 h-5" /> Host: tap Begin Round on your phone to skip ahead</motion.p>
    </div>
  );
}

function AssassinateTV({ state }) {
  const assassin = state.players.find((p) => p.id === state.assassin_id);
  useEffect(() => { audio.buzz(); }, [state.assassin_id]);
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <Skull className="w-16 h-16 text-[#FF2A85] mb-4" />
      <h1 className="font-display font-black text-4xl lg:text-6xl uppercase text-white mb-6">The Grassy Kno-all</h1>
      {assassin && <motion.div initial={{ scale: 0.7 }} animate={{ scale: 1 }} className="flex flex-col items-center gap-3"><Avatar player={assassin} size={110} /><p className="font-display font-black text-3xl text-[#00FF66]">{assassin.name}</p></motion.div>}
      <p className="font-head text-xl text-white/70 mt-6">answered fastest — choosing a rival to <span className="text-rose-400 font-bold">assassinate</span>…</p>
      <div className="flex flex-wrap items-center justify-center gap-5 mt-8 max-w-4xl">
        {state.players.map((p) => (
          <div key={p.id} className="flex flex-col items-center gap-1.5" style={{ opacity: p.lives > 0 ? 1 : 0.3 }}>
            <Avatar player={p} size={52} />
            <div className="flex gap-0.5">{Array.from({ length: 2 }).map((_, k) => <Heart key={k} className="w-3.5 h-3.5" fill={k < (p.lives || 0) ? "#FF2A85" : "transparent"} color={k < (p.lives || 0) ? "#FF2A85" : "#555"} />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NominateTV({ state }) {
  const leader = state.players.find((p) => p.id === state.nominator_id);
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <Crown className="w-16 h-16 text-[#FFD700] mb-4" fill="#FFD700" />
      <h1 className="font-display font-black text-4xl lg:text-6xl uppercase text-white mb-6">The Nominator</h1>
      {leader && <motion.div initial={{ scale: 0.7 }} animate={{ scale: 1 }} className="flex flex-col items-center gap-3"><Avatar player={leader} size={120} /><p className="font-display font-black text-3xl text-[#FFD700]">{leader.name}</p></motion.div>}
      <p className="font-head text-xl text-white/70 mt-6">is choosing to <span className="text-[#00FF66] font-bold">play</span> or <span className="text-[#FF2A85] font-bold">pass</span>…</p>
    </div>
  );
}

function StealTV({ state }) {
  const robber = state.players.find((p) => p.id === state.steal_by);
  useEffect(() => { audio.buzz(); }, [state.steal_by]);
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <Hand className="w-16 h-16 text-[#FF2A85] mb-4" />
      <h1 className="font-display font-black text-4xl lg:text-6xl uppercase text-white mb-6">Point Snatcher!</h1>
      {robber && <motion.div initial={{ scale: 0.7 }} animate={{ scale: 1 }} className="flex flex-col items-center gap-3"><Avatar player={robber} size={120} /><p className="font-display font-black text-3xl text-[#00FF66]">{robber.name}</p></motion.div>}
      <p className="font-head text-xl text-white/70 mt-6">is choosing a rival to rob 400 points from…</p>
    </div>
  );
}

function SpectatorWaiting() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <Tv className="w-20 h-20 text-[#00F0FF] mb-6" />
      <h1 className="font-display font-black text-5xl lg:text-6xl uppercase text-white mb-4">Extra screen ready</h1>
      <p className="font-head text-white/60 text-2xl max-w-2xl">This TV is linked to the game — it'll mirror everything once the host kicks things off.</p>
      <div className="mt-8 flex items-center gap-3 text-white/50"><Loader2 className="w-6 h-6 text-[#FF2A85] animate-spin" /><span className="font-head text-xl">Waiting for the host…</span></div>
    </div>
  );
}

function GetReadyTV({ round, music }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className={`font-head uppercase tracking-[0.4em] text-lg mb-8 flex items-center gap-3 ${music ? "text-[#1DB954]" : "text-[#00F0FF]"}`}>
        {music ? <><Music className="w-6 h-6" /> Name that tune</> : <><Volume2 className="w-6 h-6" /> Question coming up</>}
      </motion.p>
      <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }} className={`w-36 h-36 rounded-full flex items-center justify-center mb-9 ${music ? "bg-[#1DB954]/20" : "bg-[#00F0FF]/15"}`}>
        {music ? <Music className="w-16 h-16 text-[#1DB954]" /> : <Volume2 className="w-16 h-16 text-[#00F0FF]" />}
      </motion.div>
      <div className="flex items-end gap-2 h-16 mb-9">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <motion.span key={i} className={`w-4 rounded-full ${music ? "bg-[#1DB954]" : "bg-[#00F0FF]"}`} animate={{ height: ["25%", "100%", "40%", "85%", "25%"] }} transition={{ repeat: Infinity, duration: 0.8 + i * 0.1, ease: "easeInOut" }} style={{ height: "40%" }} />
        ))}
      </div>
      <p data-testid="tv-get-ready" className="font-display font-black text-6xl lg:text-7xl text-white">Get ready…</p>
      <p className="font-head text-white/55 text-xl mt-5">{music ? "🎧 Cueing up the snippet — listen on the TV" : "🔊 Reading the question aloud…"}</p>
      {round && <p className="font-head text-white/35 text-base mt-8">Round {round.index + 1}·Q{round.q_index + 1} / {round.q_total}</p>}
    </div>
  );
}

function QuestionView({ state, code, hostToken, spectator }) {
  const cur = state.current;
  const timeLeft = useCountdown(state.time_left, cur?.time_limit);
  const lastTick = useRef(null);
  const r = state.round;
  const isHitThat = cur?.mode === "hit_that" && !!cur?.audio_uri;
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!cur) return;
    let cancelled = false;
    setReady(false);
    // Reveal the question, start its (pre-cached) narration, and release the timer
    // — all together so the voice is guaranteed to begin as the question appears.
    const reveal = () => {
      if (cancelled) return;
      setReady(true);
      if (!isHitThat) audio.narrate(cur.spoken || cur.question);
      if (!spectator) api.post(`/rooms/${code}/question-ready`, { host_token: hostToken }).catch(() => {});
    };
    if (spectator) {
      // Extra screens follow the host's timing: reveal when the server starts it.
      return () => { cancelled = true; audio.stopNarration(); };
    }
    if (isHitThat) {
      api.post(`/rooms/${code}/spotify/play-track`, { host_token: hostToken, uri: cur.audio_uri, position_ms: cur.audio_start_ms || 0 }).catch(() => {});
      const t = setTimeout(reveal, 1800);         // let the snippet buffer
      return () => { cancelled = true; clearTimeout(t); };
    }
    const t = setTimeout(reveal, 1300);           // short pacing buffer; voice is already cached
    return () => { cancelled = true; clearTimeout(t); audio.stopNarration(); };
  }, [cur?.id]);
  // Spectators + safety net: reveal (and narrate locally) when the server has started the question.
  useEffect(() => {
    if (cur && !cur.loading && !ready) {
      setReady(true);
      if (spectator && !isHitThat) audio.narrate(cur.spoken || cur.question);
    }
  }, [cur?.loading, ready, spectator, isHitThat]);
  useEffect(() => {
    const s = Math.ceil(timeLeft);
    if (ready && s !== lastTick.current && s > 0 && timeLeft > 0.1) { lastTick.current = s; audio.tick(s <= 5); }
  }, [timeLeft, ready]);
  if (!cur) return null;
  if (!ready) return <GetReadyTV round={r} music={isHitThat} />;
  const ModeIcon = MODE_ICON[cur.mode] || Zap;
  return (
    <div className="h-full px-6 lg:px-16 py-10 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="glass rounded-full px-5 py-2 font-display font-bold text-lg border border-white/10 flex items-center gap-2"><ModeIcon className="w-5 h-5 text-[#FF2A85]" />R{r.index + 1}·Q{r.q_index + 1}<span className="text-white/40">/{r.q_total}</span></span>
          <span className="rounded-full px-5 py-2 font-head font-bold text-[#0D0714] bg-[#FFD700]">{cur.category}</span>
          {cur.mode === "hot_streaker" && <span className="rounded-full px-4 py-2 font-head font-bold text-[#0D0714] bg-[#00FF66]">Worth {HOT_STREAK_PTS[r.q_index] ?? 50} pts 🔥</span>}
          {cur.mode === "grassy_knoall" && <span className="rounded-full px-4 py-2 font-head font-bold text-white bg-rose-600">Fastest correct assassinates 💀</span>}
          {cur.mode === "hit_that" && cur.audio_uri && <span className="rounded-full px-4 py-2 font-head font-bold text-[#0D0714] bg-[#1DB954] flex items-center gap-2"><Music className="w-4 h-4" /> Name that tune!</span>}
          {state.active_player_name && <span className="rounded-full px-4 py-2 font-head font-bold text-white bg-[#8B5CF6]">{state.active_player_name}'s turn</span>}
        </div>
        <TimerRing timeLeft={timeLeft} limit={cur.time_limit} size={96} />
      </div>
      {cur.mode === "hit_that" && cur.audio_uri ? (
        <motion.div key={cur.id} initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="glass rounded-3xl p-5 lg:p-8 border border-[#1DB954]/40 mb-4 text-center flex flex-col items-center gap-4">
          <div className="flex items-end gap-1.5 h-16">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <motion.span key={i} className="w-3 rounded-full bg-[#1DB954]" animate={{ height: ["20%", "100%", "35%", "80%", "20%"] }} transition={{ repeat: Infinity, duration: 0.9 + i * 0.12, ease: "easeInOut" }} style={{ height: "40%" }} />
            ))}
          </div>
          <h2 data-testid="tv-question-text" className="font-display font-extrabold text-3xl lg:text-5xl leading-tight text-white">{cur.question}</h2>
          <p className="font-head text-white/55 text-lg">🎧 Listen on the TV — answer on your phone</p>
        </motion.div>
      ) : (
        <motion.div key={cur.id} initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass rounded-3xl p-5 lg:p-8 border border-white/10 mb-4 text-center"><h2 data-testid="tv-question-text" className="font-display font-extrabold text-2xl lg:text-4xl leading-tight text-white">{cur.question}</h2></motion.div>
      )}
      <div className="grid gap-3 flex-1 min-h-0 sm:grid-cols-2">
        {cur.options.map((opt, i) => { const o = OPTIONS[i]; return (
          <motion.div key={i} data-testid={`tv-option-${i}`} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1 + i * 0.08 }} className={`${o.bg} ${o.glow} rounded-3xl p-4 lg:p-6 flex items-center gap-4 border-4 ${o.border}`}>
            <div className="shrink-0 text-white/90"><ShapeIcon index={i} className="w-8 h-8 lg:w-12 lg:h-12" /></div><span className="font-head font-bold text-xl lg:text-3xl text-white">{opt}</span>
          </motion.div>); })}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <span data-testid="tv-answered-count" className="glass rounded-full px-5 py-2 font-display font-bold text-lg border border-white/10">{state.answered_count} answered</span>
        <div className="flex -space-x-3">{state.players.filter((p) => ((cur.mode === "last_man_standing" || cur.mode === "grassy_knoall") ? p.lives > 0 : true)).slice(0, 12).map((p) => <div key={p.id}><Avatar player={p} size={36} /></div>)}</div>
      </div>
    </div>
  );
}

function RevealView({ state }) {
  const cur = state.current;
  const ranked = state.players;
  const showLives = cur.mode === "last_man_standing" || cur.mode === "grassy_knoall";
  const maxLives = cur.mode === "grassy_knoall" ? 2 : 3;
  const ar = state.assassinate_result;
  const sr = state.steal_result;
  const activeP = state.active_player ? ranked.find((p) => p.id === state.active_player) : null;
  const nomOutcome = cur.mode === "nominator" && activeP ? {
    name: activeP.name, correct: activeP.last_correct === true, passed: state.nominate_passed,
  } : null;
  useEffect(() => {
    audio.reveal();
  }, []);
  return (
    <div className="h-full px-6 lg:px-16 py-10 flex flex-col">
      {ar && ar.target_name && <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="shrink-0 mb-3 self-center glass rounded-2xl px-6 py-2.5 border border-rose-500/40 font-head font-bold text-white flex items-center gap-2"><Skull className="w-5 h-5 text-rose-400" /> {ar.by_name} took a life from {ar.target_name}{ar.eliminated && <span className="text-rose-400 font-black">— ELIMINATED!</span>}</motion.div>}
      {sr && sr.by_name && sr.amount > 0 && <motion.div data-testid="tv-steal-result" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="shrink-0 mb-3 self-center glass rounded-2xl px-6 py-2.5 border border-[#FF2A85]/50 font-head font-bold text-white flex items-center gap-2"><Hand className="w-5 h-5 text-[#FF2A85]" /> <span className="text-[#FF2A85] font-black">{sr.by_name}</span> snatched <span className="text-[#FFD700] font-black">{sr.amount}</span> points from {sr.target_name}!</motion.div>}
      {nomOutcome && <motion.div data-testid="tv-nominator-result" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className={`shrink-0 mb-3 self-center glass rounded-2xl px-6 py-2.5 border font-head font-bold text-white flex items-center gap-2 ${nomOutcome.correct ? "border-[#00FF66]/50" : "border-rose-500/50"}`}><Crown className="w-5 h-5 text-[#8B5CF6]" /> {nomOutcome.name} {nomOutcome.passed ? "took the passed question" : "played it"} and {nomOutcome.correct ? <span className="text-[#00FF66] font-black">nailed it!</span> : <span className="text-rose-400 font-black">got it wrong</span>}</motion.div>}
      <div className="grid lg:grid-cols-2 gap-8 flex-1 min-h-0 overflow-hidden">
        <div>
          <h3 className="font-head uppercase tracking-[0.3em] text-cyan-300 text-sm mb-4">Correct answer</h3>
          <div className="space-y-3">
            {cur.options.map((opt, i) => { const o = OPTIONS[i]; const correct = i === cur.correct; return (
              <motion.div key={i} data-testid={correct ? "tv-correct-answer-reveal" : `tv-reveal-option-${i}`} initial={{ opacity: 0, x: -20 }} animate={{ opacity: correct ? 1 : 0.35, x: 0, scale: correct ? 1.02 : 1 }} className={`rounded-2xl p-5 flex items-center gap-4 border-4 ${correct ? `${o.bg} ${o.border} glow-green` : "bg-white/5 border-white/10"}`}>
                <ShapeIcon index={i} className="w-8 h-8 text-white/90" /><span className="font-head font-bold text-xl lg:text-2xl text-white flex-1">{opt}</span>{correct && <Crown className="w-7 h-7 text-[#FFD700]" fill="#FFD700" />}
              </motion.div>); })}
          </div>
        </div>
        <div>
          <h3 className="font-head uppercase tracking-[0.3em] text-cyan-300 text-sm mb-4">Leaderboard</h3>
          <div className="space-y-2">
            {ranked.map((p, i) => (
              <motion.div key={p.id} layout data-testid={`tv-leaderboard-row-${i + 1}`} transition={{ type: "spring", stiffness: 300, damping: 26 }} className="glass rounded-2xl px-4 py-3 flex items-center gap-4 border border-white/10">
                <span className="font-display font-black text-2xl w-8 text-center" style={{ color: i === 0 ? "#FFD700" : "#fff" }}>{i + 1}</span>
                <Avatar player={p} size={48} />
                <div className="flex-1 min-w-0">
                  <p className="font-head font-bold text-white truncate">{p.name}</p>
                  {showLives ? <div className="flex gap-0.5 mt-0.5">{Array.from({ length: maxLives }).map((_, k) => <Heart key={k} className="w-4 h-4" fill={k < (p.lives || 0) ? "#FF2A85" : "transparent"} color={k < (p.lives || 0) ? "#FF2A85" : "#555"} />)}</div>
                    : (<div className="flex items-center gap-2 mt-0.5">
                        {p.last_correct === true && <span className="text-[#00FF66] text-sm font-bold flex items-center gap-1"><Check className="w-3.5 h-3.5" />Correct{p.last_points > 0 && ` +${p.last_points}`}</span>}
                        {p.last_correct === false && <span className="text-rose-400/90 text-sm font-semibold flex items-center gap-1"><X className="w-3.5 h-3.5" />Wrong{p.last_points < 0 && ` ${p.last_points}`}</span>}
                      </div>)}
                </div>
                <span className="font-display font-black text-2xl text-[#00F0FF]">{p.score}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 self-center glass rounded-2xl px-6 py-2.5 border border-white/10 font-head text-white/70 flex items-center gap-2"><SkipForward className="w-5 h-5 text-[#00FF66]" /> Next question coming up…</div>
    </div>
  );
}

function BonusIntroTV({ state }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <motion.div initial={{ scale: 0.5, opacity: 0, rotate: -15 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 160 }} className="w-32 h-32 rounded-[2rem] bg-[#FFD700]/20 glow-magenta flex items-center justify-center mb-8"><Star className="w-16 h-16 text-[#FFD700]" fill="#FFD700" /></motion.div>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-head uppercase tracking-[0.4em] text-cyan-300 text-base mb-4">Bonus Round</motion.p>
      <motion.h1 initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 }} className="font-display font-black text-5xl lg:text-7xl uppercase tracking-tight bg-gradient-to-r from-[#FFD700] via-[#FF2A85] to-[#00F0FF] bg-clip-text text-transparent">Favou-Rite or Favou-Rong</motion.h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="font-head text-xl text-white/70 mt-6 max-w-2xl">Every player gets one <span className="text-[#FF2A85] font-bold">hard</span> question from their favourite topic. <span className="text-[#00FF66] font-bold">+1000</span> if you're right — <span className="text-rose-400 font-bold">−1000</span> if you're wrong!</motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="font-head text-white/40 mt-10 flex items-center gap-2"><Smartphone className="w-5 h-5" /> Host: tap Start Bonus Round on your phone</motion.p>
    </div>
  );
}

function BonusTV({ state }) {
  const b = state.bonus || {};
  const timeLeft = useCountdown(b.time_left, b.time_limit);
  const answered = new Set(b.answered_ids || []);
  const lastTick = useRef(null);
  useEffect(() => {
    const s = Math.ceil(timeLeft);
    if (s !== lastTick.current && s > 0 && timeLeft > 0.1) { lastTick.current = s; audio.tick(s <= 5); }
  }, [timeLeft]);
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <p className="font-head uppercase tracking-[0.4em] text-[#FFD700] text-base mb-5">Favou-Rite or Favou-Rong</p>
      <TimerRing timeLeft={timeLeft} limit={b.time_limit || 10} size={160} />
      <h1 className="font-display font-black text-4xl lg:text-6xl uppercase text-white mt-7">Check your phone!</h1>
      <p className="font-head text-xl text-white/70 mt-4">Answer your favourite-topic question — <span className="text-[#00FF66] font-bold">+1000</span> / <span className="text-rose-400 font-bold">−1000</span></p>
      <div data-testid="tv-bonus-answered" className="mt-8 glass rounded-full px-7 py-3 border border-white/10 font-display font-black text-3xl">{b.answered_count || 0}<span className="text-white/40"> / {b.total || 0}</span> <span className="text-base font-head text-white/60">locked in</span></div>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-7 max-w-4xl">
        {state.players.map((p) => (
          <div key={p.id} className="flex flex-col items-center gap-1" style={{ opacity: answered.has(p.id) ? 1 : 0.35 }}>
            <Avatar player={p} size={52} />
            {answered.has(p.id) && <span className="text-[#00FF66] text-xs font-head font-bold">ready</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function BonusRevealTV({ state }) {
  const ranked = state.players;
  const n = ranked.length;
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    let i = 0;
    const timer = setInterval(() => {
      i += 1;
      setShown(i);
      audio.drumroll();
      const p = ranked[n - i];
      setTimeout(() => { if (p?.last_correct === true) audio.correct(); else if (p?.last_correct === false) audio.wrong(); }, 550);
      if (i >= n) clearInterval(timer);
    }, 1500);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="h-full px-6 lg:px-16 py-10 flex flex-col">
      <h1 className="font-display font-black text-4xl lg:text-5xl uppercase text-center mb-6 bg-gradient-to-r from-[#FFD700] via-[#FF2A85] to-[#00F0FF] bg-clip-text text-transparent">Favou-Rite or Favou-Rong</h1>
      <div className="flex-1 min-h-0 overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-2 content-start">
        {ranked.map((p, i) => {
          const revealed = i >= n - shown;
          return (
            <motion.div key={p.id} layout data-testid={`tv-bonus-row-${i + 1}`} transition={{ type: "spring", stiffness: 300, damping: 26 }} className={`glass rounded-2xl px-4 py-3 flex items-center gap-4 border ${revealed ? "border-white/10" : "border-white/5 opacity-50"}`}>
              <span className="font-display font-black text-2xl w-8 text-center" style={{ color: i === 0 ? "#FFD700" : "#fff" }}>{i + 1}</span>
              <Avatar player={p} size={48} />
              <div className="flex-1 min-w-0">
                <p className="font-head font-bold text-white truncate">{p.name}</p>
                {p.bonus_category && <p className="font-head text-xs text-white/50 truncate">{p.bonus_category}</p>}
              </div>
              {!revealed ? <span className="font-display font-black text-xl text-white/30">···</span>
                : (<>
                  {p.last_correct === true && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="font-display font-black text-xl text-[#00FF66]">+{p.last_points}</motion.span>}
                  {p.last_correct === false && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="font-display font-black text-xl text-rose-400">{p.last_points}</motion.span>}
                  {p.last_correct == null && <span className="font-head text-white/40 text-sm">—</span>}
                </>)}
              <span className="font-display font-black text-2xl text-[#00F0FF] w-24 text-right">{revealed ? p.score : "···"}</span>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-4 self-center glass rounded-2xl px-6 py-2.5 border border-white/10 font-head text-white/70 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FFD700]" /> Final results coming up…</div>
    </div>
  );
}

// Lightweight, self-contained confetti (GPU transforms only, runs once, unmounts
// cleanly with the screen — no lingering canvas, no rAF loop).
function ConfettiBurst() {
  const pieces = useMemo(() => Array.from({ length: 22 }).map((_, i) => ({
    id: i, left: Math.random() * 100, delay: Math.random() * 0.5,
    color: ["#FF2A85", "#00F0FF", "#FFD700", "#00FF66", "#8B5CF6"][i % 5],
    rot: (Math.random() - 0.5) * 720, dur: 2.6 + Math.random() * 1.4, size: 9 + Math.random() * 9,
  })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
      {pieces.map((p) => (
        <motion.span key={p.id} initial={{ y: "-12%", opacity: 0, rotate: 0 }}
          animate={{ y: "112%", opacity: [0, 1, 1, 0.7], rotate: p.rot }}
          transition={{ duration: p.dur, delay: p.delay, ease: "easeIn" }}
          style={{ position: "absolute", left: `${p.left}%`, width: p.size, height: p.size * 0.42, background: p.color, borderRadius: 2 }} />
      ))}
    </div>
  );
}

function Finished({ state }) {
  const ranked = state.players;
  const podium = ranked.slice(0, 3);
  const rest = ranked.slice(3);
  useEffect(() => { audio.fanfare(); }, []);
  const order = [1, 0, 2];
  const heights = { 0: "h-56", 1: "h-40", 2: "h-32" };
  const medal = { 0: "#FFD700", 1: "#C0C0C0", 2: "#CD7F32" };
  return (
    <div className="relative h-full px-6 py-6 flex flex-col items-center justify-center overflow-hidden">
      <ConfettiBurst />
      <motion.h1 initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }} className="font-display font-black text-5xl lg:text-7xl uppercase tracking-tight mb-2 bg-gradient-to-r from-[#FFD700] via-[#FF2A85] to-[#00F0FF] bg-clip-text text-transparent">Winner!</motion.h1>
      {podium[0] && <p className="font-head text-xl text-white/70 mb-10">🎉 {podium[0].name} takes the crown 🎉</p>}
      <div data-testid="tv-winner-podium" className="flex items-end justify-center gap-4 lg:gap-8 mb-12">
        {order.map((pos) => { const p = podium[pos]; if (!p) return null; return (
          <motion.div key={p.id} initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 + pos * 0.15, type: "spring", stiffness: 160 }} className="flex flex-col items-center">
            {pos === 0 && <Crown className="w-10 h-10 mb-1 text-[#FFD700]" fill="#FFD700" />}
            <Avatar player={p} size={pos === 0 ? 110 : 84} /><p className="font-head font-bold text-white mt-3 text-lg">{p.name}</p><p className="font-display font-black text-2xl" style={{ color: medal[pos] }}>{p.score}</p>
            <div className={`${heights[pos]} w-24 lg:w-32 mt-3 rounded-t-2xl glass border-t-4 flex items-start justify-center pt-3`} style={{ borderColor: medal[pos], boxShadow: `0 0 30px ${medal[pos]}55` }}><span className="font-display font-black text-4xl" style={{ color: medal[pos] }}>{pos + 1}</span></div>
          </motion.div>); })}
      </div>
      {rest.length > 0 && <div className="w-full max-w-md space-y-2 mb-10">{rest.map((p, i) => (<div key={p.id} className="glass rounded-xl px-4 py-2.5 flex items-center gap-3 border border-white/10"><span className="font-display font-bold text-white/60 w-6">{i + 4}</span><Avatar player={p} size={36} /><span className="font-head font-semibold text-white flex-1 truncate">{p.name}</span><span className="font-display font-bold text-[#00F0FF]">{p.score}</span></div>))}</div>}
      <div className="glass rounded-2xl px-6 py-3 border border-white/10 font-head text-white/70 flex items-center gap-2"><RotateCcw className="w-5 h-5 text-[#00FF66]" /> Host: tap Play Again or Change Settings on your phone</div>
    </div>
  );
}
